import { useState } from 'react';
import { todayString } from '../../../utils/dateUtils';
import { useOrderReminders } from '../../../api/orderApi';

interface Props {
  onOpenOrder: (orderId: string) => void;
}

/** 面板条数预算；`limit` 现由服务端 `FindOrderRemindersDto` 真实生效（此前被静默剥离，面板是全量渲染） */
const PANEL_LIMIT = 20;

export default function NoteReminders({ onOpenOrder }: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem('dismissed_reminders');
      return new Set(saved ? JSON.parse(saved) : []);
    } catch (_e) {
      return new Set();
    }
  });

  // 取数交给 react-query：此前是挂载时一次性 useEffect，备注增删之后面板不动，
  // 现在写操作按 ORDER_REMINDERS_QUERY_KEY 前缀失效，面板跟着重取。
  const { data: reminders = [] } = useOrderReminders({ limit: PANEL_LIMIT });

  const dismiss = (id: string) => {
    setDismissed(prev => {
      const next = new Set(prev);
      next.add(id);
      try {
        sessionStorage.setItem('dismissed_reminders', JSON.stringify([...next]));
      } catch (_e) {
        // ignore storage errors
      }
      return next;
    });
  };

  const visible = reminders.filter(r => !dismissed.has(r.id));
  if (visible.length === 0) return null;

  const today = todayString();
  // 取满预算说明服务端还有更早的提醒，此时不能宣称「共 N 条」
  const truncated = reminders.length >= PANEL_LIMIT;

  return (
    <div className="bg-white border border-amber-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 border-b border-amber-100">
        <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
          <i className="ri-alarm-warning-line text-amber-500 text-sm" />
        </div>
        <p className="text-sm font-semibold text-amber-700 flex-1">
          备注提醒
          <span className="ml-2 text-xs font-normal text-amber-500">
            {truncated ? `最近 ${PANEL_LIMIT} 条待处理，更早的未列出` : `共 ${visible.length} 条待处理`}
          </span>
        </p>
        <button onClick={() => visible.forEach(r => dismiss(r.id))}
          className="text-xs text-amber-400 hover:text-amber-600 cursor-pointer transition-colors whitespace-nowrap">
          全部忽略
        </button>
      </div>

      <div className="divide-y divide-gray-50">
        {visible.map(r => {
          const isOverdue = r.remindAt < today;
          return (
            <div key={r.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
              <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${isOverdue ? 'bg-rose-400' : 'bg-amber-400'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                    isOverdue ? 'bg-rose-50 text-rose-500' : 'bg-amber-50 text-amber-600'
                  }`}>
                    {isOverdue ? `逾期 ${r.remindAt.slice(5)}` : `今日 ${r.remindAt.slice(5)}`}
                  </span>
                  {r.customerName && (
                    <span className="text-xs text-gray-400 truncate">{r.customerName}</span>
                  )}
                  {r.orderNumber && (
                    <span className="text-xs text-gray-300 font-mono truncate">{r.orderNumber}</span>
                  )}
                </div>
                <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">{r.content}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => onOpenOrder(r.orderId)}
                  className="text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap px-2 py-1 rounded-lg hover:bg-teal-50">
                  查看订单
                </button>
                <button onClick={() => dismiss(r.id)}
                  className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-500 cursor-pointer transition-colors rounded-lg hover:bg-gray-100">
                  <i className="ri-close-line text-sm" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
